import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import zhHans from "./locales/zh-Hans.json";

export const SUPPORTED_LANGUAGES = ["en", "zh-Hans"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  "zh-Hans": "简体中文",
};

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

function detectInitialLanguage(): SupportedLanguage {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const queryLanguage = new URLSearchParams(window.location.search).get("lang") ?? undefined;
  return coerceLanguage(
    queryLanguage ||
    window.localStorage.getItem("tokenbuddy:lang") ||
    window.navigator.language
  );
}

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      "zh-Hans": { translation: zhHans },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    lng: detectInitialLanguage(),
    // We deliberately do NOT set `nonExplicitSupportedLngs` here. With
    // it on, i18next strips the "Hans" script tag from "zh-Hans" and
    // looks for "zh" in supportedLngs, falls back to en, and silently
    // overwrites the detected language. Off: zh-Hans is matched
    // verbatim against the resources map.
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    load: "currentOnly",
    interpolation: {
      // React already escapes values; we deliberately do NOT escape here
      // because we want the `<code>tb status</code>` in the metrics.live
      // string to render as a real <code> element.
      escapeValue: false,
    },
    returnObjects: true,
  });

/** Coerce a detected language string into one of our supported values. */
export function coerceLanguage(input: string | undefined): SupportedLanguage {
  if (!input) return DEFAULT_LANGUAGE;
  const lower = input.toLowerCase();
  if (lower.startsWith("zh")) return "zh-Hans";
  if (lower.startsWith("en")) return "en";
  return DEFAULT_LANGUAGE;
}

i18n.on("languageChanged", (lng) => {
  const safe = coerceLanguage(lng);
  document.documentElement.lang = safe === "zh-Hans" ? "zh-Hans-CN" : "en";
  window.localStorage.setItem("tokenbuddy:lang", safe);
});

// Initial sync: set the html lang attribute on first paint.
document.documentElement.lang = coerceLanguage(i18n.language) === "zh-Hans"
  ? "zh-Hans-CN"
  : "en";

export const initI18n = (): Promise<typeof i18n> => {
  // Re-resolve in case the init promise hasn't fired yet.
  if (i18n.isInitialized) return Promise.resolve(i18n);
  return new Promise((resolve) => {
    i18n.on("initialized", () => resolve(i18n));
  });
};

export default i18n;

// Expose for browser dev tools and tests. Cheap; the i18next instance
// is already in memory.
if (typeof window !== "undefined") {
  (window as unknown as { i18n: typeof i18n }).i18n = i18n;
}
